/**
 * Chart-open readiness gaps (intake / insurance / card) for ENG-3443 warning modal.
 */
export type ChartReadinessGapKind = 'intake' | 'insurance' | 'card';

export interface ChartReadinessGap {
  kind: ChartReadinessGapKind;
  title: string;
  description: string;
  href: string;
}

export function chartReadinessDismissKey(clientId: string): string {
  return `unify.chartReadiness.dismissed.${clientId}`;
}

export function isChartReadinessDismissed(clientId: string): boolean {
  try {
    return sessionStorage.getItem(chartReadinessDismissKey(clientId)) === '1';
  } catch {
    return false;
  }
}

export function dismissChartReadiness(clientId: string): void {
  try {
    sessionStorage.setItem(chartReadinessDismissKey(clientId), '1');
  } catch {
    // ignore quota / private mode
  }
}

export function buildChartReadinessGaps(input: {
  clientId: string;
  hasOutstandingIntake: boolean;
  /** True when this chart has an insurance-backed episode of care. */
  isInsuranceClient: boolean;
  hasInsuranceCoverage: boolean;
  hasCardOnFile: boolean;
}): ChartReadinessGap[] {
  const gaps: ChartReadinessGap[] = [];
  const base = `/dashboard/clients/${input.clientId}`;

  if (input.hasOutstandingIntake) {
    gaps.push({
      kind: 'intake',
      title: 'Missing intake paperwork',
      description: 'One or more intake forms are still outstanding for this client.',
      href: `${base}?tab=forms`,
    });
  }

  if (input.isInsuranceClient && !input.hasInsuranceCoverage) {
    gaps.push({
      kind: 'insurance',
      title: 'Missing insurance coverage',
      description: 'This client has an insurance episode but no coverage on file.',
      href: `${base}/profile?tab=billing`,
    });
  }

  if (!input.hasCardOnFile) {
    gaps.push({
      kind: 'card',
      title: 'Missing credit card info',
      description: 'No card on file was found for this client.',
      href: `${base}/profile?tab=billing`,
    });
  }

  return gaps;
}
